import type { ILanguageResources, ITranslationModule } from '../../interfaces';
import { interpolate } from '../../global';
import {
  V6ConfigEntryPoint,
  validateConfigVerison,
  v6ConfigManager
} from '../../../webmodule-common/interop/webmodule-interop';

class Id {
  private constructor() {}
  public static readonly prefix = 'UI.scripts.v6config.V6Config.';
  public static readonly supplierSystemUpdate = `${Id.prefix}supplierSystemUpdate`;
  public static readonly forceReloadRequired = `${Id.prefix}forceReloadRequired`;
  public static readonly frameConfigRequiresReload = `${Id.prefix}frameConfigRequiresReload`;
  public static readonly pageReloadRequired = `${Id.prefix}pageReloadRequired`;
  public static readonly v6OptimizedBlobStorageInfo = `${Id.prefix}v6OptimizedBlobStorageInfo`;
  // Frame compare localization entries
  public static readonly frameCompareNoAttributeChanges = `${Id.prefix}frameCompareNoAttributeChanges`;
  public static readonly frameCompareAttributeAdded = `${Id.prefix}frameCompareAttributeAdded`;
  public static readonly frameCompareAttributeRemoved = `${Id.prefix}frameCompareAttributeRemoved`;
  public static readonly frameCompareAttributeChanged = `${Id.prefix}frameCompareAttributeChanged`;
  public static readonly frameCompareFrameRemoved = `${Id.prefix}frameCompareFrameRemoved`;
  public static readonly frameCompareFrameAdded = `${Id.prefix}frameCompareFrameAdded`;
  public static readonly frameCompareBreadcrumbTitle = `${Id.prefix}frameCompareBreadcrumbTitle`;
}

export class LangV6Config {
  private constructor() {}
  public static supplierSystemUpdate(): string {
    return interpolate(Id.supplierSystemUpdate);
  }
  public static forceReloadRequired(): string {
    return interpolate(Id.forceReloadRequired);
  }
  public static frameConfigRequiresReload(): string {
    return interpolate(Id.frameConfigRequiresReload);
  }
  public static pageReloadRequired(): string {
    return interpolate(Id.pageReloadRequired);
  }
  public static v6OptimizedBlobStorageInfo(): string {
    return interpolate(Id.v6OptimizedBlobStorageInfo);
  }
  // Frame compare methods
  public static frameCompareNoAttributeChanges(): string {
    return interpolate(Id.frameCompareNoAttributeChanges);
  }
  public static frameCompareAttributeAdded(params: { label: string; value: string }): string {
    return interpolate(Id.frameCompareAttributeAdded, params);
  }
  public static frameCompareAttributeRemoved(params: { label: string; value: string }): string {
    return interpolate(Id.frameCompareAttributeRemoved, params);
  }
  public static frameCompareAttributeChanged(params: { label: string; before: string; after: string }): string {
    return interpolate(Id.frameCompareAttributeChanged, params);
  }
  public static frameCompareFrameRemoved(): string {
    return interpolate(Id.frameCompareFrameRemoved);
  }
  public static frameCompareFrameAdded(): string {
    return interpolate(Id.frameCompareFrameAdded);
  }
  public static frameCompareBreadcrumbTitle(params: { breadcrumb: string }): string {
    return interpolate(Id.frameCompareBreadcrumbTitle, params);
  }
}

export class V6ConfigModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(
      Id.supplierSystemUpdate,
      "The {{glossary 'supplier'}} has updated their systems and the page requires a force reload now to update the systems",
      'Message for supplier system update requiring reload'
    );
    register.add(Id.forceReloadRequired, 'Force Reload Required', 'Dialog title for force reload required');
    register.add(
      Id.frameConfigRequiresReload,
      "{{glossary 'frame'}} configuration requires a page reload",
      'Message for frame config requiring reload'
    );
    register.add(Id.pageReloadRequired, 'Page reload required', 'Dialog title for page reload required');
    // Frame compare entries
    register.add(
      Id.frameCompareNoAttributeChanges,
      '_No attribute changes_',
      'Markdown line when there are no attribute changes in frame compare'
    );
    register.add(
      Id.frameCompareAttributeAdded,
      '- **{{label}}** was added with **{{value}}**',
      'Line describing an added attribute in frame compare'
    );
    register.add(
      Id.frameCompareAttributeRemoved,
      '- **{{label}}** with **{{value}}** was removed',
      'Line describing a removed attribute in frame compare'
    );
    register.add(
      Id.frameCompareAttributeChanged,
      '- **{{label}}** changed from **{{before}}** to **{{after}}**',
      'Line describing a changed attribute in frame compare'
    );
    register.add(
      Id.frameCompareFrameRemoved,
      '- Frame was **removed**',
      'Line describing a removed frame in frame compare'
    );
    register.add(Id.frameCompareFrameAdded, '- Frame was **added**', 'Line describing an added frame in frame compare');
    register.add(
      Id.frameCompareBreadcrumbTitle,
      '### {{breadcrumb}}',
      'Title line for breadcrumb path in nested frame compare'
    );
    register.add(
      Id.v6OptimizedBlobStorageInfo,
      'V6 optimized blob storage is enabled to improve load times for large assets. v2.1.24 or later of the Frame Config Module is required to fully support this feature.',
      'Informational text shown when optimized blob storage is active'
    );
  }
}

export function v6ConfigEntryPoint(version?: string): V6ConfigEntryPoint {
  validateConfigVerison(version);
  if (!version)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return v6ConfigManager()!.activeVersion!;
  return v6ConfigManager()!.ver(version)!;
}
